// @steered AudibleSecurityContext 1.2 2026-08-17

import type {
  AppetizeClient,
  AppetizeError,
  AppetizeQueueData,
  AppetizeSdk,
  AppetizeSession,
  AppetizeSessionConfig,
} from './appetize.types';

export const APPETIZE_SDK_URL = 'https://js.appetize.io/embed.js';
export const APPETIZE_HOST_URL = 'https://audible.appetize.io';
export const APPETIZE_HEARTBEAT_MS = 60_000;

export type AppetizeSessionState =
  | { status: 'loading-sdk' }
  | { status: 'starting' }
  | { status: 'queued'; position: number }
  | { status: 'ready' }
  | { status: 'disconnected' }
  | { status: 'ended' }
  | { status: 'failed'; message: string };

interface ControllerOptions {
  iframeId: string;
  config: AppetizeSessionConfig;
  onStateChange: (state: AppetizeSessionState) => void;
}

export interface AppetizeSessionController {
  ready: Promise<void>;
  dispose: () => Promise<void>;
}

let sdkPromise: Promise<AppetizeSdk> | null = null;
const clientPromises = new Map<string, Promise<AppetizeClient>>();

const errorMessage = (error: unknown): string => {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'Unable to start the native app preview.';
};

export const loadAppetizeSdk = (): Promise<AppetizeSdk> => {
  if (window.appetize) {
    return Promise.resolve(window.appetize);
  }
  if (sdkPromise) {
    return sdkPromise;
  }

  sdkPromise = new Promise<AppetizeSdk>((resolve, reject) => {
    let script = document.querySelector<HTMLScriptElement>(`script[src="${APPETIZE_SDK_URL}"]`);

    const handleLoad = () => {
      if (window.appetize) {
        resolve(window.appetize);
      } else {
        sdkPromise = null;
        reject(new Error('Appetize SDK loaded without exposing its client API.'));
      }
    };
    const handleError = () => {
      sdkPromise = null;
      reject(new Error('Unable to load the Appetize SDK. Check the hosting Content Security Policy.'));
    };

    if (!script) {
      script = document.createElement('script');
      script.src = APPETIZE_SDK_URL;
      script.async = true;
      script.dataset.appetizeSdk = 'true';
      document.head.appendChild(script);
    }

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
  });

  return sdkPromise;
};

const getClientOnce = (iframeId: string, config: AppetizeSessionConfig): Promise<AppetizeClient> => {
  const existing = clientPromises.get(iframeId);
  if (existing) {
    return existing;
  }

  const promise = loadAppetizeSdk()
    .then((sdk) => sdk.getClient(`#${iframeId}`, config))
    .catch((error) => {
      clientPromises.delete(iframeId);
      throw error;
    });
  clientPromises.set(iframeId, promise);
  return promise;
};

export const createAppetizeSession = ({
  iframeId,
  config,
  onStateChange,
}: ControllerOptions): AppetizeSessionController => {
  let client: AppetizeClient | null = null;
  let session: AppetizeSession | null = null;
  let heartbeatId: ReturnType<typeof setInterval> | null = null;
  let disposed = false;
  let disposePromise: Promise<void> | null = null;

  const emit = (state: AppetizeSessionState) => {
    if (!disposed) {
      onStateChange(state);
    }
  };

  const stopHeartbeat = () => {
    if (heartbeatId !== null) {
      clearInterval(heartbeatId);
      heartbeatId = null;
    }
  };

  const onQueue = (queue: AppetizeQueueData) => emit({ status: 'queued', position: queue.position });
  const onQueueEnd = () => emit({ status: 'starting' });
  const onSessionEnded = () => {
    stopHeartbeat();
    emit({ status: 'ended' });
  };
  const onSessionError = (error: AppetizeError) => emit({ status: 'failed', message: errorMessage(error) });
  const onSessionEnd = () => {
    stopHeartbeat();
    emit({ status: 'ended' });
  };
  const onSessionDisconnect = () => {
    stopHeartbeat();
    emit({ status: 'disconnected' });
  };

  const removeListeners = () => {
    if (client) {
      client.off('queue', onQueue);
      client.off('queueEnd', onQueueEnd);
      client.off('sessionEnded', onSessionEnded);
    }
    if (session) {
      session.off('error', onSessionError);
      session.off('end', onSessionEnd);
      session.off('disconnect', onSessionDisconnect);
    }
  };

  const initialize = async () => {
    try {
      emit({ status: 'loading-sdk' });
      client = await getClientOnce(iframeId, config);
      if (disposed) {
        await client.endSession().catch(() => undefined);
        return;
      }

      client.on('queue', onQueue);
      client.on('queueEnd', onQueueEnd);
      client.on('sessionEnded', onSessionEnded);
      emit({ status: 'starting' });

      let rejectClientError: (error: Error) => void = () => undefined;
      const clientError = new Promise<never>((_, reject) => {
        rejectClientError = reject;
      });
      const onClientError = (error: AppetizeError) => rejectClientError(new Error(errorMessage(error)));
      client.on('error', onClientError);

      try {
        session = await Promise.race([client.startSession(), clientError]);
      } finally {
        client.off('error', onClientError);
      }

      if (disposed) {
        await session.end().catch(() => undefined);
        return;
      }

      session.on('error', onSessionError);
      session.on('end', onSessionEnd);
      session.on('disconnect', onSessionDisconnect);
      await session.waitUntilReady();

      if (disposed) {
        await session.end().catch(() => undefined);
        return;
      }

      heartbeatId = setInterval(() => {
        session?.heartbeat().catch(() => {
          stopHeartbeat();
          emit({ status: 'disconnected' });
        });
      }, APPETIZE_HEARTBEAT_MS);
      emit({ status: 'ready' });
    } catch (error) {
      clientPromises.delete(iframeId);
      emit({ status: 'failed', message: errorMessage(error) });
    }
  };

  const ready = initialize();

  const dispose = (): Promise<void> => {
    if (disposePromise) {
      return disposePromise;
    }
    disposed = true;
    stopHeartbeat();
    removeListeners();
    clientPromises.delete(iframeId);
    disposePromise = (session ? session.end() : client?.endSession() ?? Promise.resolve()).catch(() => undefined);
    return disposePromise;
  };

  return { ready, dispose };
};

export const resetAppetizeManagerForTests = (): void => {
  sdkPromise = null;
  clientPromises.clear();
};
